import { useOidcUser } from '@axa-fr/react-oidc';
import { HOUR_IN_MILLISECONDS } from 'config';
import { useMeQuery } from 'providers/api';
import { intersection, is } from 'ramda';
import { ReactNode } from 'react';
import { i18n } from 'i18n';
import Loading from 'components/Loading';
import { isAdminUser } from 'utils/roles';
import AnalyticsIdentifier from 'providers/analytics/AnalyticsIdentifier';
import AuthorisationContext, { BackfillPlannerUser, IAuthorisationContext } from './context';
import { BackfillPlannerRole, hasPermission, Permission } from './permissions';

const ROLE_ALIASES: Record<string, BackfillPlannerRole> = {
  patersonandcooke: BackfillPlannerRole.PatersonCooke,
};

const findRoleByName = (roleString: string): BackfillPlannerRole | undefined => {
  const normalizedInput = roleString.replace(/\s/g, '').toLowerCase();
  if (ROLE_ALIASES[normalizedInput] !== undefined) {
    return ROLE_ALIASES[normalizedInput];
  }
  const enumKey = Object.keys(BackfillPlannerRole).find(
    (key) => key.toLowerCase() === normalizedInput,
  );
  return enumKey ? BackfillPlannerRole[enumKey as keyof typeof BackfillPlannerRole] : undefined;
};

export interface AuthorisationProviderProps {
  children: ReactNode;
}

export default function AuthorisationProvider({ children }: AuthorisationProviderProps) {
  const { oidcUser } = useOidcUser();
  const { data, isFetching } = useMeQuery(undefined, {
    staleTime: HOUR_IN_MILLISECONDS,
  });

  if (!oidcUser) {
    return <Loading />;
  }

  const { name, email, given_name: givenName } = oidcUser;

  // Access roles from custom claims
  const oidcUserRoles = (oidcUser as any).roles || (oidcUser as any).role || [];

  // If there is only one roles it comes in as just a sting.
  const roles = is(String, oidcUserRoles) ? [oidcUserRoles] : oidcUserRoles;

  if (!name) {
    throw new Error(i18n._('oidcUser does not contain expected prop \'name\''));
  }

  if (!email) {
    throw new Error(i18n._('oidcUser does not contain expected prop \'email\''));
  }

  if (!roles) {
    throw new Error(i18n._('oidcUser does not contain expected prop \'roles\''));
  }

  if (!((is(Array, roles) && (roles as any[]).every(is(String))))) {
    throw new Error(i18n._('oidcUser does not contain the correct type for \'roles\''));
  }

  const user: BackfillPlannerUser = {
    id: oidcUser.sub,
    username: name,
    name: givenName ?? name,
    email,
    roles: roles as string[],
    permissions: {
      teams: data?.teams ?? [],
      teamRoles: (data?.teamroles as string[] | undefined)?.reduce((teamRoles, teamRoleString) => {
        const [teamId, roleString] = teamRoleString.split('_');
        const role = findRoleByName(roleString);
        if (role === undefined) {
          return teamRoles;
        }
        return [
          ...teamRoles,
          {
            teamId,
            role,
          },
        ];
      }, [] as BackfillPlannerUser['permissions']['teamRoles']) ?? [],
    },
  };

  const isAdmin = () => isAdminUser(user.roles);

  const validatePermission = (permission: Permission, ids: string[]) => {
    if (isAdmin()) {
      return true;
    }

    return user.permissions.teamRoles
      .some(({ teamId, role }) => {
        if (!ids.includes(teamId)) {
          return false;
        }

        return hasPermission(role, permission);
      });
  };

  const canViewTeam = (teamIds: string[]) => isAdmin() || intersection(user.permissions.teams, teamIds).length > 0;

  const value: IAuthorisationContext = {
    state: { user, loading: isFetching },
    actions: {},
    functions: {
      isAdmin,
      canViewTeam,
      validatePermission,
    },
  };

  return (
    <AuthorisationContext.Provider value={value}>
      <AnalyticsIdentifier />
      {children}
    </AuthorisationContext.Provider>
  );
}
